import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { LuSearch, LuX } from 'react-icons/lu';
import { ReportDateRangePicker } from 'components/common';
import { CATEGORY_OPTIONS, eventLabel } from './auditLogLabels';

// One toolbar row: search grows, the selects keep a fixed width, the date range
// is a compact trigger with presets. Wraps onto a second line on narrow screens.
const CONTROL_CLS = [
  'tw-h-9 tw-px-3 tw-text-sm tw-text-slate-800 tw-bg-white tw-rounded-md',
  'tw-border tw-border-solid tw-border-slate-300 hover:tw-border-slate-400',
  'focus:tw-outline-none focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100 tw-transition-colors',
].join(' ');

const FilterSelect = ({ value, onChange, label, children, width }) => (
  <select className={`${CONTROL_CLS} tw-cursor-pointer ${width}`} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
    {children}
  </select>
);

FilterSelect.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  width: PropTypes.string.isRequired,
};

// Search is applied on Enter / blur, not on every keystroke: each change is a
// new query on the audit table.
const AuditLogFilterBar = ({ filters, actions, onChange, onReset, hasCustomRange }) => {
  const [q, setQ] = useState(filters.q || '');
  useEffect(() => { setQ(filters.q || ''); }, [filters.q]);

  const set = (patch) => onChange({ ...filters, ...patch });
  const commitSearch = () => { if ((filters.q || '') !== q) set({ q }); };

  const actionOptions = actions.filter((a) => !filters.category || a.category === filters.category);
  // Dates always have a value (the 30-day default), so they only count once changed.
  const hasFilters = ['category', 'outcome', 'action_name', 'q', 'include_system', 'user_id', 'project_id', 'ip']
    .some((key) => filters[key]) || hasCustomRange;

  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      <div className="tw-relative tw-flex-1 tw-min-w-[240px]">
        <LuSearch className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400" size={15} />
        <input
          className={`${CONTROL_CLS} tw-w-full tw-pl-9 placeholder:tw-text-slate-400`}
          placeholder="Search user, description, event…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onBlur={commitSearch}
          onKeyDown={(e) => { if (e.key === 'Enter') commitSearch(); }}
          aria-label="Search audit logs"
        />
      </div>

      <FilterSelect
        label="Category"
        width="tw-w-44"
        value={filters.category || ''}
        onChange={(category) => set({ category, action_name: '' })}
      >
        <option value="">All categories</option>
        {CATEGORY_OPTIONS.filter((o) => o.value !== 'system').map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
        <option value="system">System (jobs)</option>
      </FilterSelect>

      <FilterSelect label="Event" width="tw-w-52" value={filters.action_name || ''} onChange={(action_name) => set({ action_name })}>
        <option value="">All events</option>
        {actionOptions.map((a) => (
          <option key={`${a.category}-${a.action}`} value={a.action}>{eventLabel(a.action)} ({a.count})</option>
        ))}
      </FilterSelect>

      <FilterSelect label="Outcome" width="tw-w-36" value={filters.outcome || ''} onChange={(outcome) => set({ outcome })}>
        <option value="">Any outcome</option>
        <option value="success">Succeeded</option>
        <option value="failure">Failed / refused</option>
      </FilterSelect>

      <ReportDateRangePicker
        align="right"
        startDate={filters.from || ''}
        endDate={filters.to || ''}
        onChange={({ startDate, endDate }) => set({ from: startDate || '', to: endDate || '' })}
      />

      {hasFilters && (
        <button
          type="button"
          onClick={onReset}
          className="tw-inline-flex tw-items-center tw-gap-1 tw-h-9 tw-px-2.5 tw-text-sm tw-font-medium tw-text-slate-500 hover:tw-text-slate-800 tw-bg-transparent tw-border-0 tw-cursor-pointer"
        >
          <LuX size={14} /> Clear filters
        </button>
      )}
    </div>
  );
};

AuditLogFilterBar.propTypes = {
  filters: PropTypes.object.isRequired,
  actions: PropTypes.arrayOf(PropTypes.shape({ category: PropTypes.string, action: PropTypes.string, count: PropTypes.number })),
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  hasCustomRange: PropTypes.bool,
};

AuditLogFilterBar.defaultProps = { actions: [], hasCustomRange: false };

export default AuditLogFilterBar;
